import { InfoCircleOutlined } from '@ant-design/icons';
import { Col, DatePicker, Input, Row, Select, Tooltip, Typography } from 'antd';
import moment from 'moment';
import { Trans, useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';

import useAPIQuery from '../api/useAPIQuery';
import {
  SankhyaCompany,
  SankhyaDepartment,
  SankhyaJobRole,
  VixtingAffiliate,
  VixtingUnitBusiness,
} from '../types';
import Form from './Form';

// FIXME: Change names in the future
interface VixtingAdmissionFormProps {
  sankhyaCompanies: SankhyaCompany[];
  sankhyaJobRoles: SankhyaJobRole[];
  sankhyaDepartments: SankhyaDepartment[];
  affiliates?: VixtingAffiliate[];
  unitBusinesses?: VixtingUnitBusiness[];
}

const VixtingAdmissionForm = ({
  sankhyaCompanies,
  sankhyaJobRoles,
  sankhyaDepartments,
  affiliates = [],
  unitBusinesses = [],
}: VixtingAdmissionFormProps) => {
  const { t } = useTranslation();

  const vixting_admission_types = [
    { key: 'Aprendiz', name: t('Aprendiz') },
    { key: 'Autônomo(a)', name: t('Autônomo(a)') },
    { key: 'CLT', name: t('CLT') },
    { key: 'Contrato por tempo indeterminado', name: t('Contrato por tempo indeterminado') },
    { key: 'Empregado(a) Doméstico(a)', name: t('Empregado(a) Doméstico(a)') },
    { key: 'Estagiário(a)', name: t('Estagiário(a)') },
    { key: 'Estatutário(a)', name: t('Estatutário(a)') },
    { key: 'Indeterminado', name: t('Indeterminado') },
    { key: 'Intermitente', name: t('Intermitente') },
    { key: 'PJ', name: t('PJ') },
    { key: 'Sócio(a) - Divisão de Lucros', name: t('Sócio(a) - Divisão de Lucros') },
    { key: 'Sócio(a) - Pró Labore', name: t('Sócio(a) - Pró Labore') },
    { key: 'Temporário', name: t('Temporário') },
    { key: 'Voluntário(a)', name: t('Voluntário(a)') },
    { key: 'CLT Parcial', name: t('CLT Parcial') },
    { key: 'CLT - Variável', name: t('CLT - Variável') },
    { key: 'CLT - Sem controle de jornada', name: t('CLT - Sem controle de jornada') },
    { key: 'CLT Home Office - Fixo', name: t('CLT Home Office - Fixo') },
    {
      key: 'CLT Home Office - Sem controle de jornada',
      name: t('CLT Home Office - Sem controle de jornada'),
    },
    { key: 'Contrato por tempo determinado', name: t('Contrato por tempo determinado') },
    { key: 'Cooperado', name: t('Cooperado') },
  ];

  const config = useAPIQuery('/configs/tenant-configurations/');

  return (
    <>
      <Form.Item label={t('Regime de trabalho')} name="admissionType" rules={[{ required: true }]}>
        <Select
          showSearch
          filterOption={(input, option) =>
            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
          }
          data-testid="select-vixting-job-role"
        >
          {vixting_admission_types.map((admissionType) => (
            <Select.Option key={admissionType.key}>{`${admissionType.name}`}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            label={
              <Typography.Text>
                <Trans>Data limite</Trans>{' '}
                <Tooltip
                  title={
                    <div style={{ textAlign: 'center' }}>
                      <Trans>
                        Data máxima para concluir o processo de admissão deste candidato no Vlow.
                      </Trans>
                    </div>
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </Typography.Text>
            }
            name="vixtingLimitDate"
            shouldUpdate
            rules={[{ required: true }]}
          >
            <DatePicker
              disabledDate={(current) => {
                // Dias anteriores ao dia de hoje não são permitidos
                return current && current < moment().startOf('day');
              }}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('Celular')} name="candidatePhone">
            <NumberFormat format="(##) #####-####" customInput={Input} disabled placeholder="N/A" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="E-mail" name="candidateEmail" rules={[{ required: true }]}>
        <Input disabled />
      </Form.Item>
      <Form.Item label="CPF" name="candidateCPF" rules={[{ required: true }]}>
        <NumberFormat
          format="###.###.###-##"
          customInput={Input}
          data-testid="candidate-cpf-input"
          disabled
        />
      </Form.Item>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item label={t('Empresa')} name="sankhyaCompany" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
              disabled
              data-testid="select-sankhya-company"
            >
              {sankhyaCompanies.map(({ CODEMP, Empresa_NOMEFANTASIA }) => (
                <Select.Option key={CODEMP} value={`${CODEMP}`}>
                  <span title={CODEMP}>{`${Empresa_NOMEFANTASIA}`}</span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('Filial')} name="affiliate" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
              disabled
              data-testid="select-vixting-affiliate"
            >
              {affiliates.map((affiliate) => (
                <Select.Option key={affiliate.id} value={`${affiliate.id}`}>
                  <span title={affiliate.id}>{`${affiliate.fantasyName}`}</span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      {config.data?.enableVixtingUnitBusiness && (
        <Form.Item label={t('Unidade de Negócio')} name="unitBusiness">
          <Select
            showSearch
            filterOption={(input, option) =>
              (option?.label as string).toLowerCase().includes(input.toLowerCase())
            }
            disabled
            data-testid="select-vixting-unitBusiness"
            options={unitBusinesses.map((unitBusiness) => ({
              value: `${unitBusiness.id}`,
              label: unitBusiness.name,
              key: unitBusiness.id,
            }))}
          />
        </Form.Item>
      )}
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item label={t('Cargo')} name="sankhyaJobRole" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
              disabled
              data-testid="select-sankhya-job-role"
            >
              {sankhyaJobRoles.map(({ CODCARGO, DESCRCARGO }) => (
                <Select.Option key={CODCARGO} value={`${CODCARGO}`}>
                  {`${CODCARGO} - ${DESCRCARGO}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t('Departamento')}
            name="sankhyaDepartment"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
              disabled
              data-testid="select-sankhya-department"
            >
              {sankhyaDepartments.map(({ CODDEP, DESCRDEP }) => (
                <Select.Option key={CODDEP} value={`${CODDEP}`}>
                  {`${CODDEP} - ${DESCRDEP}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default VixtingAdmissionForm;
